// СТРОКИ ШАГА 6 (overlay) — таблицы борда как чистые функции, данными, без экрана. B8-2.
//
// ИСТОЧНИК БУКВЫ — банк `docs/design/wizard-s6/copy-bank.md` (§1 механизм, §2 бровь), байт борда
// `Life Map - s6 Overlay v10.html` (`browLabel` / `MECH` / `mechLine` / `carriersCard`) — порт
// поведения, а строки — из банка. Сверка машинная: `src/render/wizardS6Copy.test.js`.
//
// ⚠️ `MECH` ЭТОГО ЭКРАНА И `MECH` §V — РАЗНЫЕ ТАБЛИЦЫ. У §V (`src/copy/chartCopy.js`) это хвост имени
// паттерна (`, on its own` · ` trade-off` …), здесь — механизм несущей (`one layer carried it` …).
// Общих строк у них нет; сводить нельзя. Из общего узла экран берёт italic под чартом — форму
// overlay `ITALIC_OVERLAY` (U+2019, `s553.d1`), она реэкспортируется ниже.
//
// ⛔ ТРИ ДОЛГА БОРДА ЗДЕСЬ УЖЕ ПОГАШЕНЫ — узел печатает решение, не байт:
//   · `s531.d3` — карточка при `scatter` = `no single line carries it.` и ничего больше
//     (борд клеит к ней чужое сказуемое `field`);
//   · `s824.d5` — бровь `scatter` = `none leads` (борд объединяет ветку с `field` → `the field`);
//   · `s711.d2` — ярлык поля цитаты `didn’t allow yourself` через U+2019 (данные борда несут U+0027).
//
// ⛔ ПЕЧАТНОЕ ИМЯ СЛОЯ НЕ ВЫБИРАЕТСЯ ЗДЕСЬ. Две строки брови из семи — данные (`copy-bank.md` §2):
// имена слоёв приходят из движка, и то, как их печатать, решает экран. Функции берут `lname`
// параметром — то же правило, что в `findingBank.js` и `chartCopy.js`.

export { ITALIC_OVERLAY } from '../../copy/chartCopy.js';

/** Механизм карточки `the carriers`, после тире — банк §1. `split` ветвится по числу несущих. */
export const OVERLAY_MECH = {
  singleton: 'one layer carried it',
  pair: 'two layers carried it together',
  triplet: 'three layers carried it together',
  split: 'two layers, pulling apart',
  field: 'the whole field moved together',
};
/** `split` при несущих > 2 (`s464.d2`). */
export const OVERLAY_MECH_SPLIT_WIDE = 'every layer took a side';
/** Карточка при `scatter` — одна фраза, целиком (`s531.d3`). */
export const OVERLAY_SCATTER_CARD = 'no single line carries it.';

/** Бровь `overlay · {…}` — строки банка §2 (без двух строк-данных). */
export const OVERLAY_BROW = {
  triplet: 'three threads',
  splitWide: 'a trade-off, all along',
  field: 'the field',
  /** `s824.d5`: у `scatter` своя бровь, ветка с `field` разведена. */
  scatter: 'none leads',
};

/** Строка механизма; для `scatter` механизма нет — `null` (карточка печатает одну фразу). */
export function mechLine(carrierConfig, members = []) {
  if (carrierConfig === 'scatter') return null;
  if (carrierConfig === 'split' && members.length > 2) return OVERLAY_MECH_SPLIT_WIDE;
  return OVERLAY_MECH[carrierConfig] ?? null;
}

/** Бровь `overlay · {browLabel}`. `lname` — печатное имя слоя у экрана. */
export function browLabel(carrierConfig, members, lname) {
  const m = members || [];
  switch (carrierConfig) {
    case 'singleton': return lname(m[0]);
    case 'pair': return m.map(lname).join(' & ');
    case 'triplet': return OVERLAY_BROW.triplet;
    case 'split': return m.length > 2 ? OVERLAY_BROW.splitWide : `${lname(m[0])} against ${lname(m[1])}`;
    case 'field': return OVERLAY_BROW.field;
    case 'scatter': return OVERLAY_BROW.scatter;
    default: return null;
  }
}

/** Зачин карточки `the carriers` перед тире — банк §1, у всех раскладов, кроме `scatter`. */
export const OVERLAY_CARRIERS_LEDE = 'one unbroken curve across the whole life';

/**
 * Карточка `the carriers`: `{ scatter, sentence, mech, pills }`.
 *   · `scatter` — `sentence` = одна фраза `s531.d3`, `mech` нет, пилюль нет;
 *   · прочие — `sentence` = `{зачин} — {механизм}.` (тире U+2014), `mech` отдельно — борд кладёт его
 *     в `<em>`, и экрану нужна граница; `pills` = несущие (данные).
 */
export function carriersCard(carrierConfig, members = []) {
  if (carrierConfig === 'scatter') return { scatter: true, sentence: OVERLAY_SCATTER_CARD, mech: null, pills: [] };
  const mech = mechLine(carrierConfig, members);
  return { scatter: false, sentence: `${OVERLAY_CARRIERS_LEDE} — ${mech}.`, mech, pills: members.slice() };
}

/** Строка `evidence · stages` печатается при любом раскладе, кроме `scatter` (`s849.d1`). */
export const showEvidenceLine = (carrierConfig) => carrierConfig !== 'scatter';

/** Ярлык поля цитаты по T-коду — типографский апостроф на экране визарда (`s711.d2`). */
export const OVERLAY_FIELD = { T1: 'about', T2: 'knew but did otherwise', T3: 'didn’t allow yourself' };
